import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import {
  AppCard,
  AppDeleteModal,
  AppInput,
  AppMultiSelect,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
} from "@/layout/AppComponents";
import styled from "styled-components";
import { axiosPath, path } from "../../../../routing/helpers/router";
import Translator from "../../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import ModuleComsaEnvironmentalSideBarReports from "../ModuleComsaEnvironmentalSideBarReports";
import AppSubmitButton from "../../../../layout/components/form/AppSubmitButton";
import errorHandler from "@/layout/components/error/ErrorHandler";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";

export default function ModuleComsaEnvironmentalIndustrialDataInputScreen() {
  const [deleteSectionPassword] = useState("mjKr3@i4");
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();
  const [showModalDeleteSectionModal, setShowModalDeleteSectionModal] =
    useState();

  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="no" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="yes" />
      ),
      value: 1,
    },
  ]);
  const [controlInfluenceOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="control" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="influence" />
      ),
      value: 1,
    },
  ]);
  const [constructionScopeOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="urban" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="rural" />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="special" />
      ),
      value: 2,
    },
  ]);

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          { moduleBridgeHex, subModuleBridgeHex, plantHex, sheet: "dataInput" }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "dataInput",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  // > DELETE SECTION //
  const handleDeleteSection = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_delete"),
          { moduleBridgeHex, subModuleBridgeHex, plantHex }
        )
        .then((r) => {
          dispatch(
            currentPageAction.change("moduleData", r.data?.moduleData, true)
          );
          navigate(
            path({
              name: "user_module_comsaEnvironmental_home",
              params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
            })
          );
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < DELETE SECTION //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="industrial"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_industrial_data_input" />
        <Title className="mb-4">
          <Translator file="module-comsaEnvironmental" name="data.collection" />
        </Title>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppCard
                  header={
                    <>
                      <span>
                        <Translator
                          file="module-comsaEnvironmental"
                          name="general.data.work"
                        />
                      </span>
                      <span
                        className="btn btn-danger btn-xs float-right"
                        onClick={() => setShowModalDeleteSectionModal(true)}
                      >
                        <AppRemixIcon icon="delete-bin-6" />
                      </span>
                    </>
                  }
                  className="mb-2"
                >
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="construction"
                      />
                    )}
                    onChange={(value) => setFieldValue("construction", value)}
                    value={values.construction}
                    error={errors.construction}
                    isClearable
                  />
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="name"
                      />
                    )}
                    onChange={(value) => setFieldValue("name", value)}
                    value={values.name}
                    error={errors.name}
                    isClearable
                  />
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="contract"
                      />
                    )}
                    onChange={(value) => setFieldValue("contract", value)}
                    value={values.contract}
                    error={errors.contract}
                    isClearable
                  />
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="client"
                      />
                    )}
                    onChange={(value) => setFieldValue("client", value)}
                    value={values.client}
                    error={errors.client}
                    isClearable
                  />
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="scheduled.start"
                      />
                    )}
                    onChange={(value) => setFieldValue("plannedStart", value)}
                    value={values.plannedStart}
                    error={errors.plannedStart}
                    isClearable
                  />
                  <AppInput
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="scheduled.duration.months"
                      />
                    }
                    onChange={(value) => setFieldValue("plannedLength", value)}
                    value={values.plannedLength}
                    error={errors.plannedLength}
                    isClearable
                  />
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="address"
                      />
                    )}
                    onChange={(value) => setFieldValue("address", value)}
                    value={values.address}
                    error={errors.address}
                    isClearable
                  />
                  <AppMultiSelect
                    options={constructionScopeOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="select.scope.project.indicate.all.affected.scopes"
                      />
                    )}
                    value={values?.constructionScope || []}
                    error={errors?.constructionScope}
                    onChange={(opt) => setFieldValue("constructionScope", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="is.there.design.stage"
                      />
                    )}
                    value={values?.designStage}
                    error={errors?.designStage}
                    onChange={(opt) => setFieldValue("designStage", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={controlInfluenceOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="use.operation.maintenance.conservation.stage.capacity"
                      />
                    )}
                    value={values?.exploitationStageCapacityOf}
                    error={errors?.exploitationStageCapacityOf}
                    onChange={(opt) =>
                      setFieldValue("exploitationStageCapacityOf", opt)
                    }
                    isClearable
                  />
                  {values?.exploitationStageCapacityOf === 0 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="fill.tab"
                      />
                      <b>
                        <Translator
                          file="module-comsaEnvironmental"
                          name="construction.normal.aspects"
                        />
                      </b>
                      .
                    </div>
                  )}
                  {values?.exploitationStageCapacityOf === 1 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="fill.tab"
                      />{" "}
                      <b>
                        <Translator
                          file="module-comsaEnvironmental"
                          name="construction.normal.aspects"
                        />
                      </b>{" "}
                      <Translator file="module-comsaEnvironmental" name="y" />{" "}
                      <b>
                        <Translator
                          file="module-comsaEnvironmental"
                          name="use.normal.aspects"
                        />
                      </b>
                      .
                    </div>
                  )}
                  <AppSingleSelect
                    options={controlInfluenceOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="during.deconstruction/dismantling.stage.capacity"
                      />
                    }
                    value={values?.deconstructionStageCapacityOf}
                    error={errors?.deconstructionStageCapacityOf}
                    onChange={(opt) =>
                      setFieldValue("deconstructionStageCapacityOf", opt)
                    }
                    isClearable
                  />

                  {values?.deconstructionStageCapacityOf === 0 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="fill.tab"
                      />{" "}
                      <b>
                        <Translator
                          file="module-comsaEnvironmental"
                          name="construction.normal.aspects"
                        />
                      </b>
                      .
                    </div>
                  )}
                  {values?.deconstructionStageCapacityOf === 1 && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-comsaEnvironmental"
                        name="fill.tab"
                      />{" "}
                      <b>
                        <Translator
                          file="module-comsaEnvironmental"
                          name="construction.normal.aspects"
                        />
                      </b>{" "}
                      <Translator file="module-comsaEnvironmental" name="y" />{" "}
                      <b>
                        <Translator
                          file="module-comsaEnvironmental"
                          name="deconstruction.normals"
                        />
                      </b>
                      .
                    </div>
                  )}
                </AppCard>
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
      <AppDeleteModal
        show={showModalDeleteSectionModal}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-comsaEnvironmental"
            name="delete.all.project.data"
          />
        )}
        onClose={() => setShowModalDeleteSectionModal(false)}
        onConfirm={handleDeleteSection}
        sensitive
        sensitiveWord={deleteSectionPassword}
        sensitiveText={
          <Translator
            file="module-comsaEnvironmental"
            name="enter.the.password"
          />
        }
        password
      />
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;
